/**
 * Client-side CAPA list filtering (used by CapaList + CapaListFilterDrawer).
 */
import {
  normalizeIsfTemplateCategoryForApi,
} from "./capaConstants";

export const CAPA_LIST_FILTER_INITIAL = {
  titleContains: "",
  /** "" = all modules; else module value from CAPA row */
  moduleFilter: "",
  /** Study management: multiple study entity ids (context) */
  studyManagementStudyIds: [],
  /** ISF: single study (loads document list) */
  isfStudyFilter: "",
  /** Staff id (user_management) */
  staffFilter: "",
  /** eISF / ePSF (isf) */
  isfCategoryFilter: "",
  /** ISF: multiple document entity ids (context) */
  documentFilters: [],
  sourceTypes: [],
  risks: [],
  statuses: [],
  /** Owner filter from staff API (StaffId values) */
  ownerStaffIds: [],
  /** Backward-compat for older drafts */
  ownerNames: [],
  createdFrom: "",
  createdTo: "",
  myCapasOnly: false,
};

/**
 * @param {typeof CAPA_LIST_FILTER_INITIAL} f
 */
export function countActiveFilters(f) {
  let n = 0;
  if (String(f.titleContains || "").trim()) n += 1;
  if (String(f.moduleFilter || "").trim()) n += 1;
  n += (f.studyManagementStudyIds || []).length;
  if (String(f.isfStudyFilter || "").trim()) n += 1;
  if (String(f.staffFilter || "").trim()) n += 1;
  if (String(f.isfCategoryFilter || "").trim()) n += 1;
  n += (f.documentFilters || []).length;
  n += (f.sourceTypes || []).length;
  n += (f.risks || []).length;
  n += (f.statuses || []).length;
  n += (f.ownerStaffIds || []).length;
  if (String(f.createdFrom || "").trim()) n += 1;
  if (String(f.createdTo || "").trim()) n += 1;
  if (f.myCapasOnly) n += 1;
  return n;
}

function moduleValueFromRow(row) {
  const m = row.Module;
  if (m && String(m).trim()) return String(m).trim();
  const label = row.ModuleLabel;
  return label ? String(label).trim().toLowerCase().replace(/\s+/g, "_") : "";
}

function ownerDisplay(row, ownerFallbackName, userData) {
  if (row.OwnerName === "You (current user)") {
    return (
      ownerFallbackName ||
      userData?.Name ||
      row.OwnerName ||
      "—"
    );
  }
  return row.OwnerName || "—";
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {typeof CAPA_LIST_FILTER_INITIAL} filters
 */
export function filterCapaRows(rows, filters, ownerFallbackName, userData) {
  if (!Array.isArray(rows)) return [];

  return rows.filter((row) => {
    const qTitle = String(filters.titleContains || "").trim().toLowerCase();
    if (qTitle && !String(row.Title || "").toLowerCase().includes(qTitle)) {
      return false;
    }

    const mod = String(filters.moduleFilter || "").trim();
    if (mod) {
      const rowMod = moduleValueFromRow(row);
      if (rowMod !== mod) return false;
    }

    if (mod === "study_management") {
      const ids = filters.studyManagementStudyIds || [];
      if (
        ids.length &&
        !ids.map(String).includes(String(row.EntityId || ""))
      ) {
        return false;
      }
    }

    if (mod === "user_management") {
      const st = String(filters.staffFilter || "").trim();
      if (st && String(row.EntityId || "") !== st) return false;
    }

    if (mod === "isf") {
      const study = String(filters.isfStudyFilter || "").trim();
      if (study) {
        const rowStudy = String(
          row.StudyIdForIsf || row.IsfStudyId || "",
        ).trim();
        if (rowStudy !== study) return false;
      }
      const cat = String(filters.isfCategoryFilter || "").trim();
      if (cat) {
        const rowCat = normalizeIsfTemplateCategoryForApi(
          row.TemplateCategoryForIsf || row.TemplateCategory || "",
        );
        if (rowCat !== cat) return false;
      }
      const docs = filters.documentFilters || [];
      if (
        docs.length &&
        !docs.map(String).includes(String(row.EntityId || ""))
      ) {
        return false;
      }
    }

    if (
      filters.sourceTypes.length &&
      !filters.sourceTypes.includes(row.SourceType)
    ) {
      return false;
    }

    const risk = String(row.Risk || "Medium").trim() || "Medium";
    if (filters.risks.length && !filters.risks.includes(risk)) {
      return false;
    }

    if (filters.statuses.length && !filters.statuses.includes(row.Status)) {
      return false;
    }

    const ownerIds = (filters.ownerStaffIds || []).map((x) =>
      String(x).toLowerCase(),
    );
    if (ownerIds.length) {
      const rowOwnerId = String(row.OwnerStaffId || "").toLowerCase();
      if (!ownerIds.includes(rowOwnerId)) return false;
    } else if ((filters.ownerNames || []).length) {
      // Legacy support for already-saved drafts that used owner display names.
      const disp = ownerDisplay(row, ownerFallbackName, userData);
      if (!(filters.ownerNames || []).includes(disp)) return false;
    }

    const from = String(filters.createdFrom || "").trim();
    if (from) {
      const t = new Date(row.CreatedAt).getTime();
      const start = new Date(from);
      start.setHours(0, 0, 0, 0);
      if (Number.isNaN(t) || t < start.getTime()) return false;
    }

    const to = String(filters.createdTo || "").trim();
    if (to) {
      const t = new Date(row.CreatedAt).getTime();
      const end = new Date(to);
      end.setHours(23, 59, 59, 999);
      if (Number.isNaN(t) || t > end.getTime()) return false;
    }

    if (filters.myCapasOnly && userData?.StaffId) {
      const sid = String(row.OwnerStaffId || "").toLowerCase();
      const my = String(userData.StaffId).toLowerCase();
      if (sid !== my) return false;
    }

    return true;
  });
}
